/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [spaces.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 22.03.20, 16:18
 */
import React, {Fragment, useEffect, useState} from 'react';
import {useAuthorization, useSiteConfiguration} from "../../selectors";
import {formatFns, isCalendarDateWithinInterval, useLanguage} from "../../components/configuration/configuration";
import {SidebarHeadline} from "../../components/navigation/links/nav-headlines";
import EventToggler from "../../components/toggler/event-toggler";
import {VIEW_TYPE_ALL, VIEW_TYPE_EVENTS, VIEW_TYPE_SPACES} from "../../components/hooks/async-fetch-view-spaces";
import {DateRange, Event, EventAvailable} from "@material-ui/icons";
import usePeekGenericSpaces from "../../components/hooks/use-peek-generic-spaces";
import usePeekEventSpaces from "../../components/hooks/use-peek-event-spaces";
import {getPublicUser} from "../../actions/environment";
import {SUCCESS_STATUS} from "../../actions";
import {Spinner} from "../../components/util/spinner";
import SidebarEntryPeekSpace from "../../components/billboard-sidebar/lists/sidebar-entry-peekspace";
import CalendarViewEntryPeekEvent from "./calendar-view-entry-peekevent";

const CURRENT_MONTH = 'CURRENT_MONTH';
const TODAY_ONLY = 'TODAY_ONLY';
const ALL_EVENTS = 'ALL_EVENTS';
const CATEGORY_EVENT = 'EVENT';
const CATEGORY_FESTIVAL = 'FESTIVAL';

/**
 * @param username
 * @param location
 * @param type 'events', 'spaces' or 'all' - displays events and spaces
 */
const CalendarView = ({username, location, type = VIEW_TYPE_ALL, category}) => {
    const [showAllEvents, setShowAllEvents] = useState(false);
    const [showEvents, setShowEvents] = useState(CURRENT_MONTH);
    const {isAuthorized, principalUsername, isSuperUser} = useAuthorization();
    const {calendarPage, siteUsername} = useSiteConfiguration();
    const {peekGenericSpaces, spaces: peekSpaces, status: spacesStatus} = usePeekGenericSpaces();
    const {peekEventSpaces, events: peekEvents, status: eventsStatus} = usePeekEventSpaces();

    const Lang = useLanguage();

    useEffect(() => {
        const {fetchname, isParent} = resolveNameByLocation();
        isRenderSpaces && peekGenericSpaces(fetchname, isParent);
        (isRenderEvents || isRenderFestivals) && peekEventSpaces(fetchname, isParent);
    }, [username, location]);

    // useEffect(() => {
    //     setShowAllEvents(true);
    // }, [location]);

    const resolveNameByLocation = () => {
        if (isAuthorized && username) {
            return calendarPage === location.pathname
                ? {fetchname: siteUsername, isParent: true}
                : {fetchname: username, isParent: !!isSuperUser};
        }
        return {fetchname: getPublicUser(), isParent: true}
    }

    // builds a Map with key('YYYY-mm') and value([events]) from first date in calendarEvents
    // an entry represents a year-month with a corresponding array of events
    const buildEventsByMonth = () => {
        const periodEvents = new Map();

        peekEvents.forEach(event => {
            if (event.calendarDate) {
                const dates = event.calendarDate.date.split('-');
                const key = `${dates[0]}-${dates[1]}`;

                const bucket = periodEvents.get(key);
                bucket ? bucket.push(event) : periodEvents.set(key, [event]);
            }
        })
        return periodEvents;
    }

    // if category is present then consider only matching events
    const renderEventsByMonth = (period, eventsByMonth) => {

        const today = new Date();
        const events = eventsByMonth
            .filter(event => (category === null || event.category === null) || event.category.toLowerCase() === category)
            .filter(event => {
                return showEvents === TODAY_ONLY ? isCalendarDateWithinInterval(today,
                    event.calendarDate,
                    event.calendarDateEnd) : true;
             })
            .map(event => {
            return <div className='col-md-4' key={event.id}>
                <CalendarViewEntryPeekEvent peekspace={event}/>
            </div>
            })

        const monthYear = formatFns(new Date(period + '-01'), 'MMMM yyyy');
        const current = formatFns(new Date(), 'MMMM yyyy');

        return (showEvents === ALL_EVENTS) || (monthYear === current) ?
            <EventToggler key={period}
                          title={monthYear}
                          visible={monthYear === current}
                          icon={<Event/>}>
                <div className='row'>{events}</div>
            </EventToggler> : null

    }

    const renderEvents = () => {
        const eventsByMonth = buildEventsByMonth();
        let togglerEvents = [];
        eventsByMonth.forEach((value, key) => {
            togglerEvents = [...togglerEvents, renderEventsByMonth(key, value)];
        });

        return togglerEvents.length > 0
            ? <div>{togglerEvents}</div>
            : <div className='mb-2'>{Lang.nav.sidebar.events.none}</div>
    }

    const renderSpaces = () => {
        const spaces = peekSpaces.map(space => {
            return <div key={space.id} className='col-md-6 mb-2' style={{margin: '0', padding: '0 5px 0 5px'}}>
                <SidebarEntryPeekSpace peekspace={space} size='bigger' text={space.description}/>
            </div>
        })
        return spaces.length > 0
            ? <div className='row'>{spaces}</div>
            : <div className='mb-2'>{Lang.nav.sidebar.spaces.none}</div>
    };

    const isRenderEvents = (type === VIEW_TYPE_ALL || type === VIEW_TYPE_EVENTS) && category.toUpperCase() === CATEGORY_EVENT;
    const isRenderFestivals = (type === VIEW_TYPE_ALL || type === VIEW_TYPE_EVENTS) && category.toUpperCase() === CATEGORY_FESTIVAL;
    const isRenderSpaces = type === VIEW_TYPE_ALL || type === VIEW_TYPE_SPACES;
    const isPending =
        (isRenderSpaces && spacesStatus !== SUCCESS_STATUS) ||
        (isRenderEvents && eventsStatus !== SUCCESS_STATUS) ||
        (isRenderFestivals && eventsStatus !== SUCCESS_STATUS);

    if (isPending) return <Spinner/>;

    const isGenericView = !isAuthorized || username !== principalUsername;
    const icon = isGenericView ? <DateRange/> : <EventAvailable/>;

    // TODO sidebar must take an object not a string
    return <div className='spaces-view-container overflow-x-hidden'>
        {isRenderEvents && <Fragment>
            <SidebarHeadline icon={icon}
                             title={isGenericView ? Lang.nav.sidebar.events.superUser : Lang.nav.sidebar.events.yours}>
                <div className="btn-group btn-group-sm mt-1" role="group" aria-label="Basic radio toggle button group">
                    <input type="radio" className="btn-check" name="showevents" id="currentMonthId" autoComplete="off"
                           onChange={() => setShowEvents(CURRENT_MONTH)}
                           checked={showEvents === CURRENT_MONTH}/>
                    <label className="btn btn-outline-dark" htmlFor="currentMonthId">
                        {Lang.nav.sidebar.events.currentMonth}</label>

                    <input type="radio" className="btn-check" name="showevents" id="todayEventsId" autoComplete="off"
                           onChange={() => setShowEvents(TODAY_ONLY)}
                           checked={showEvents === TODAY_ONLY}/>
                    <label className="btn btn-outline-dark" htmlFor="todayEventsId">
                        {Lang.nav.sidebar.events.today}</label>

                    <input type="radio" className="btn-check" name="showevents" id="allEventsId" autoComplete="off"
                           onChange={() => setShowEvents(ALL_EVENTS)}
                           checked={showEvents === ALL_EVENTS}/>
                    <label className="btn btn-outline-dark" htmlFor="allEventsId">
                        {Lang.nav.sidebar.events.allEvents}</label>
                </div>
            </SidebarHeadline>
            {peekEvents && renderEvents()}
        </Fragment>}

        {isRenderFestivals && <Fragment>
            <SidebarHeadline icon={icon}
                             title={isGenericView ? Lang.nav.sidebar.festivals.superUser : Lang.nav.sidebar.festivals.yours}>
                <div className="btn-group btn-group-sm mt-1" role="group" aria-label="Basic radio toggle button group">
                    <input type="radio" className="btn-check" name="showevents" id="currentMonthId" autoComplete="off"
                           onChange={() => setShowEvents(CURRENT_MONTH)}
                           checked={showEvents === CURRENT_MONTH}/>
                    <label className="btn btn-outline-dark" htmlFor="currentMonthId">
                        {Lang.nav.sidebar.festivals.currentMonth}</label>

                    <input type="radio" className="btn-check" name="showevents" id="todayEventsId" autoComplete="off"
                           onChange={() => setShowEvents(TODAY_ONLY)}
                           checked={showEvents === TODAY_ONLY}/>
                    <label className="btn btn-outline-dark" htmlFor="todayEventsId">
                        {Lang.nav.sidebar.festivals.today}</label>

                    <input type="radio" className="btn-check" name="showevents" id="allEventsId" autoComplete="off"
                           onChange={() => setShowEvents(ALL_EVENTS)}
                           checked={showEvents === ALL_EVENTS}/>
                    <label className="btn btn-outline-dark" htmlFor="allEventsId">
                        {Lang.nav.sidebar.festivals.allEvents}</label>
                </div>
            </SidebarHeadline>
            {peekEvents && renderEvents()}
        </Fragment>}

        {isRenderSpaces && <Fragment>
            <SidebarHeadline className='mt-2' icon={icon}
                             title={isGenericView ? Lang.nav.sidebar.spaces.superUser : Lang.nav.sidebar.spaces.yours}/>
            {peekSpaces && renderSpaces()}
        </Fragment>}
    </div>
}

export default CalendarView;
